/**
 * Kampagnentest im Chat — dieselbe Vorschau wie „Kampagne testen“ in der App.
 *
 * Die Karte trägt nur Kampagne und Test-ID. Die Nachrichten holt sie aus der gemeinsamen
 * Testablage (GET /campaigns/:id/test/latest), in die App- und MCP-Tests schreiben. Läuft
 * der Test noch (mit gpt-6.1-sol dauert er oft länger als eine Chat-Antwort), fragt sie
 * nach, bis er fertig ist.
 */
import { Paper, Stack, Typography } from '@mui/material';
import { getLatestCampaignTest } from 'API/linkedin-funnel';
import MessagePreview from 'features/co-pilot/campaign-builder/campaign-building-panel/campaign-testing/message-preview';
import { TestContactPreview } from 'features/co-pilot/campaign-builder/campaign-builder.types';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

export interface CampaignTestCardData {
  campaignId: string;
  testId?: string | null;
  status?: 'running' | 'done';
}

const POLL_MS = 5000;
const MAX_POLLS = 120; // 10 Minuten

const CampaignTestCard: React.FC<{ data: CampaignTestCardData }> = ({ data }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [testContacts, setTestContacts] = useState<TestContactPreview[]>([]);
  const [status, setStatus] = useState<'loading' | 'running' | 'done' | 'failed' | 'missing'>('loading');
  const [contactIndex, setContactIndex] = useState(0);
  const [messageIndex, setMessageIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let polls = 0;
    const load = async () => {
      const res = await getLatestCampaignTest(data.campaignId);
      if (cancelled) return;
      const latest = res?.response;
      if (!latest || res?.error) {
        setStatus('missing');
        return;
      }
      if (latest.status === 'running' && polls < MAX_POLLS) {
        polls += 1;
        setStatus('running');
        timer = setTimeout(load, POLL_MS);
        return;
      }
      if (latest.status === 'failed' || latest.status === 'interrupted') {
        setStatus('failed');
        return;
      }
      setTestContacts(Array.isArray(latest.testContacts) ? latest.testContacts : []);
      setStatus('done');
    };
    load();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [data.campaignId, data.testId]);

  const messageCount = testContacts[contactIndex]?.messages?.length ?? 0;
  const step = (current: number, size: number, delta: number) => (size ? (current + delta + size) % size : 0);

  return (
    <Paper className={classes.card}>
      <Stack gap={1}>
        <Typography variant="subtitle2">{t('testCampaign')}</Typography>
        {status === 'failed' && (
          <Typography variant="body2" color="error">
            {t('testFailedShort', 'Der Test ist fehlgeschlagen — bitte erneut starten.')}
          </Typography>
        )}
        {status === 'missing' && (
          <Typography variant="body2" color="textSecondary">
            {t('testNoResult', 'Kein Testergebnis gefunden — Ergebnisse werden 2 Stunden aufbewahrt.')}
          </Typography>
        )}
        {(status === 'loading' || status === 'running' || status === 'done') && (
          <div className={classes.preview}>
            <MessagePreview
              testContacts={testContacts}
              noNodesInFlow={false}
              notEveryNodeHasFunctionName={false}
              loading={status !== 'done'}
              activeTestContactIndex={contactIndex}
              activeMessageIndex={messageIndex}
              handleNextContact={() => {
                setContactIndex((i) => step(i, testContacts.length, 1));
                setMessageIndex(0);
              }}
              handlePrevContact={() => {
                setContactIndex((i) => step(i, testContacts.length, -1));
                setMessageIndex(0);
              }}
              handleNextMessage={() => setMessageIndex((i) => step(i, messageCount, 1))}
              handlePrevMessage={() => setMessageIndex((i) => step(i, messageCount, -1))}
            />
          </div>
        )}
      </Stack>
    </Paper>
  );
};

const useStyles = makeStyles()((theme) => ({
  card: {
    padding: theme.spacing(1.5),
    borderRadius: 8,
    backgroundColor: '#fff',
    border: `1px solid ${theme.palette.divider}`,
    maxWidth: 720,
  },
  preview: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 360,
  },
}));

export default CampaignTestCard;
